/**
 * Predictive preloading for what the listener is about to reach.
 *
 * When an episode ends, the next one's artwork is needed immediately, and fetching it then
 * is a visible pop-in on a slow connection. Warming it while the current episode still has
 * minutes left costs one idle-time request and makes the handover look instant.
 *
 * **Artwork only, deliberately.** An earlier version of this module also warmed the next
 * enclosure with `<link rel=preload as=fetch>`, on the assumption that a preload hint is
 * cheaper than a real fetch. It is not: `rel=preload` issues a plain GET with no `Range`
 * header and pulls the *entire* response body into the memory cache. Measured against a 4 MB
 * enclosure it transferred all 4 MB — so a hostile or merely careless feed could have had
 * every listener silently download a multi-gigabyte "episode" in the background, with no
 * gesture, no progress UI and no way to abort. Worse, it bought nothing even when it worked:
 * the hint went out in CORS mode (`crossOrigin`) while `<audio>` loads the same URL in
 * no-cors mode, so the two requests land in different cache partitions and the audio element
 * re-downloaded the file from scratch. Media stays with the `<audio>` element, which knows
 * how to range-request it.
 *
 * Everything that remains is deliberately cheap: artwork is warmed with `new Image()`, which
 * reuses the same HTTP cache entry the `<img>` will hit, and each URL is warmed at most once.
 * Data Saver and metered connections opt out entirely — spending someone's cellular
 * allowance to save them 200ms is the wrong trade.
 */

const warmed = new Set<string>();

/** How many upcoming items to warm. Beyond a couple the hit rate stops justifying the bytes. */
const LOOKAHEAD = 2;

/**
 * Ceiling on the remembered-URL set.
 *
 * Only bounds a slow leak rather than an attack: at most `LOOKAHEAD` URLs enter per queue
 * change, so a feed cannot drive this on its own. Still, a long session with a feed that
 * rotates artwork URLs should not grow a set forever.
 */
const MAX_WARMED = 200;

interface PreloadTarget {
  imageUrl?: string | null;
  feedImageUrl?: string | null;
}

interface NetworkInformation {
  saveData?: boolean;
  effectiveType?: string;
}

/** True when the browser tells us bytes are expensive right now. */
function bytesAreCheap(): boolean {
  const connection = (navigator as Navigator & { connection?: NetworkInformation }).connection;
  if (!connection) return true;
  if (connection.saveData) return false;
  return connection.effectiveType !== 'slow-2g' && connection.effectiveType !== '2g';
}

/**
 * Artwork URLs come from the publisher's RSS, so they are arbitrary strings rather than
 * URLs we have any guarantee about. A scheme allowlist keeps anything exotic — `data:`,
 * `blob:`, or a relative path that would resolve against our own origin — out of a
 * background request the listener never asked for and cannot see.
 *
 * This is not the app's only defence and is not claimed to be: the same URL reaches an
 * `<img src>` when the row renders (see `components/Artwork.tsx`). It exists so that
 * *speculative* fetching, which happens with no user action at all, is never broader than
 * the rendering path it is anticipating.
 */
function isWarmableUrl(raw: string): boolean {
  try {
    // Parsed with no base on purpose: a relative or junk string would otherwise resolve
    // against our own origin and warm a same-origin URL that is certain to 404.
    const { protocol } = new URL(raw);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

function whenIdle(task: () => void): void {
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => number })
    .requestIdleCallback;
  if (typeof idle === 'function') idle(task);
  else setTimeout(task, 200);
}

function warmImage(url: string | null | undefined): void {
  if (!url || warmed.has(url) || !isWarmableUrl(url)) return;
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  } catch {
    // A blocked or malformed URL is not worth reporting — the real <img> will fall back, and
    // leaving the URL unmarked means a later attempt is free to try again.
    return;
  }
  // Oldest-first eviction: `Set` preserves insertion order, so the first key is the coldest.
  if (warmed.size >= MAX_WARMED) warmed.delete(warmed.values().next().value as string);
  warmed.add(url);
}

/**
 * Warm the next few items the listener is likely to reach.
 *
 * Safe to call on every `upNext` change: already-warmed URLs are skipped, so a queue reorder
 * that leaves the head unchanged does no work at all.
 */
export function preloadUpcoming(items: readonly PreloadTarget[]): void {
  // Also the guard that keeps this inert under the node test pool and in any non-DOM host.
  if (typeof document === 'undefined' || typeof navigator === 'undefined') return;
  if (items.length === 0 || !bytesAreCheap()) return;
  const targets = items.slice(0, LOOKAHEAD);
  whenIdle(() => {
    for (const item of targets) warmImage(item.imageUrl ?? item.feedImageUrl);
  });
}

/** Test seam: forget what has been warmed so far. */
export function resetPreloadCache(): void {
  warmed.clear();
}
